import React, { useState } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Grid,
  Card,
  CardContent,
  Divider,
  Tabs,
  Tab,
  Button,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const Costs = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Initial investment costs
  const initialInvestmentData = [
    { category: 'Software Development', cost: 2500000, description: 'Initial app development and testing' },
    { category: 'Hardware', cost: 800000, description: 'Servers and development equipment' },
    { category: 'Legal & Administrative', cost: 350000, description: 'Business registration, legal fees, patents' },
    { category: 'Marketing & Branding', cost: 600000, description: 'Initial marketing campaign, branding' },
    { category: 'Office Setup', cost: 450000, description: 'Office space, furniture, utilities setup' },
    { category: 'API Integration Costs', cost: 300000, description: 'Clarifai, OpenAI, DeepAI integration' },
  ];

  // Monthly operating costs
  const monthlyOperatingData = [
    { category: 'Salaries & Benefits', cost: 1200000, description: 'Development, support, and administrative staff' },
    { category: 'Server & Hosting', cost: 180000, description: 'Cloud hosting, database, storage costs' },
    { category: 'API Usage Fees', cost: 250000, description: 'Clarifai, OpenAI, DeepAI usage fees' },
    { category: 'Office Rent & Utilities', cost: 120000, description: 'Office space and associated utilities' },
    { category: 'Marketing', cost: 200000, description: 'Ongoing marketing and user acquisition' },
    { category: 'Customer Support', cost: 150000, description: 'Support tools and personnel' },
    { category: 'Maintenance & Updates', cost: 180000, description: 'Regular app updates and maintenance' },
    { category: 'Miscellaneous', cost: 80000, description: 'Unforeseen expenses and contingencies' },
  ];

  // Revenue projections
  const revenueProjectionData = [
    { year: 'Year 1', subscription: 3600000, consultation: 1800000, marketplace: 900000, premium: 1200000 },
    { year: 'Year 2', subscription: 7200000, consultation: 3600000, marketplace: 2400000, premium: 3000000 },
    { year: 'Year 3', subscription: 14400000, consultation: 7200000, marketplace: 5400000, premium: 7200000 },
    { year: 'Year 4', subscription: 21600000, consultation: 10800000, marketplace: 9000000, premium: 12600000 },
    { year: 'Year 5', subscription: 30000000, consultation: 15000000, marketplace: 13500000, premium: 18000000 },
  ];

  // Pricing model
  const pricingModelData = [
    { plan: 'Basic', price: 'Free', features: 'Limited AI diagnostics, Community access, Basic hardware analysis' },
    { plan: 'Standard', price: '1,500 DZD/month', features: 'Full AI diagnostics, Priority support, Complete hardware analysis' },
    { plan: 'Premium', price: '3,500 DZD/month', features: 'All Standard features, Video consultations, Repair guides, Parts discounts' },
    { plan: 'Enterprise', price: 'Custom', features: 'Custom solutions, Dedicated support, API access, White-labeling options' },
  ];

  // Break-even analysis
  const breakEvenData = [
    { month: 'Month 1', revenue: 500000, costs: 2360000 },
    { month: 'Month 6', revenue: 1500000, costs: 2360000 },
    { month: 'Month 12', revenue: 3000000, costs: 2360000 },
    { month: 'Month 18', revenue: 4500000, costs: 2360000 },
    { month: 'Month 24', revenue: 6000000, costs: 2360000 },
    { month: 'Month 30', revenue: 7500000, costs: 2360000 },
  ];

  // Pie chart data for revenue streams
  const revenueStreamsData = [
    { name: 'Subscriptions', value: 45 },
    { name: 'Video Consultations', value: 20 },
    { name: 'Parts Marketplace', value: 15 },
    { name: 'Premium Services', value: 20 },
  ];

  const COLORS = ['#007C3F', '#00A651', '#FFE302', '#FFC107'];

  // Format number with commas and DZD
  const formatCurrency = (value) => {
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") + " DZD";
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4, bgcolor: '#ffffff' }}>
      <Paper elevation={0} sx={{ p: 3, mb: 4, backgroundColor: '#000000', color: '#ffffff', borderRadius: 0 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ fontWeight: 'bold', fontFamily: 'Arial Black, Arial, sans-serif', letterSpacing: '1px' }}>
          Warrili Business Financial Plan
        </Typography>
        <Typography variant="subtitle1" gutterBottom align="center" sx={{ mb: 3, fontFamily: 'Arial, sans-serif', letterSpacing: '0.5px' }}>
          Comprehensive financial analysis and projections for Warrili technical support platform
        </Typography>
      </Paper>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          variant={isMobile ? "scrollable" : "fullWidth"}
          scrollButtons={isMobile ? "auto" : false}
          sx={{ 
            '& .MuiTab-root': { 
              fontWeight: 'bold',
              color: '#000000',
              fontFamily: 'Arial, sans-serif',
              fontSize: '15px',
              '&.Mui-selected': { color: '#000000', fontWeight: 'bold' }
            },
            '& .MuiTabs-indicator': { backgroundColor: '#FFE302', height: '3px' }
          }}
        >
          <Tab label="Initial Investment" />
          <Tab label="Operating Costs" />
          <Tab label="Revenue Projections" />
          <Tab label="Pricing Model" />
          <Tab label="Break-Even Analysis" />
        </Tabs>
      </Box>

      {/* Initial Investment Tab */}
      {tabValue === 0 && (
        <Box>
          <Typography variant="h5" gutterBottom sx={{ color: '#000000', fontWeight: 'bold', borderBottom: '2px solid #FFE302', pb: 1, fontFamily: 'Arial Black, Arial, sans-serif', letterSpacing: '0.5px' }}>
            Initial Investment Requirements
          </Typography>
          <Typography variant="body1" paragraph sx={{ fontFamily: 'Arial, sans-serif', fontSize: '16px', mb: 3 }}>
            The following table outlines the initial capital investment required to launch the Warrili platform, including software development, hardware, and other startup costs.
          </Typography>
          
          <TableContainer component={Paper} sx={{ mb: 4, borderRadius: 0, boxShadow: 'none', border: '1px solid #000000' }}>
            <Table>
              <TableHead sx={{ backgroundColor: '#000000' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', color: '#FFE302', borderBottom: 'none', fontFamily: 'Arial Black, Arial, sans-serif', fontSize: '16px' }}>Category</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#FFE302', borderBottom: 'none', fontFamily: 'Arial Black, Arial, sans-serif', fontSize: '16px' }}>Cost (DZD)</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#FFE302', borderBottom: 'none', fontFamily: 'Arial Black, Arial, sans-serif', fontSize: '16px' }}>Description</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {initialInvestmentData.map((row, index) => (
                  <TableRow key={index} sx={{ 
                    '&:nth-of-type(odd)': { backgroundColor: '#f5f5f5' },
                    '&:hover': { backgroundColor: 'rgba(255, 227, 2, 0.05)' }
                  }}>
                    <TableCell sx={{ borderColor: '#e0e0e0', fontFamily: 'Arial, sans-serif', fontWeight: 600, fontSize: '15px' }}>{row.category}</TableCell>
                    <TableCell sx={{ borderColor: '#e0e0e0', fontFamily: 'Arial, sans-serif', fontSize: '15px' }}>{formatCurrency(row.cost)}</TableCell>
                    <TableCell sx={{ borderColor: '#e0e0e0', fontFamily: 'Arial, sans-serif', fontSize: '15px' }}>{row.description}</TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ backgroundColor: 'rgba(255, 227, 2, 0.15)' }}>
                  <TableCell sx={{ fontWeight: 'bold', borderColor: '#e0e0e0', fontFamily: 'Arial Black, Arial, sans-serif', fontSize: '16px' }}>Total Initial Investment</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', borderColor: '#e0e0e0', fontFamily: 'Arial Black, Arial, sans-serif', fontSize: '16px' }}>
                    {formatCurrency(initialInvestmentData.reduce((sum, item) => sum + item.cost, 0))}
                  </TableCell>
                  <TableCell sx={{ borderColor: '#e0e0e0' }}></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card sx={{ height: '100%', borderRadius: 0, boxShadow: 'none', border: '1px solid #000000' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#000000', borderBottom: '2px solid #FFE302', pb: 1, fontFamily: 'Arial Black, Arial, sans-serif', fontSize: '18px' }}>
                    Key Investment Insights
                  </Typography>
                  <Typography variant="body2" paragraph sx={{ fontFamily: 'Arial, sans-serif', fontSize: '15px', fontWeight: 500 }}>
                    • Software development represents the largest portion of initial investment (50%), reflecting the technology-focused nature of the Warrili platform.
                  </Typography>
                  <Typography variant="body2" paragraph sx={{ fontFamily: 'Arial, sans-serif', fontSize: '15px', fontWeight: 500 }}>
                    • API integration costs include setup for Clarifai, OpenAI, and DeepAI services that power our hardware analysis features.
                  </Typography>
                  <Typography variant="body2" paragraph sx={{ fontFamily: 'Arial, sans-serif', fontSize: '15px', fontWeight: 500 }}>
                    • Marketing investment is significant to ensure proper market penetration and user acquisition in the early stages.
                  </Typography>
                  <Typography variant="body2" paragraph sx={{ fontFamily: 'Arial, sans-serif', fontSize: '15px', fontWeight: 500 }}>
                    • Legal and administrative costs include intellectual property protection for our AI algorithms and diagnostic tools.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Operating Costs Tab */}
      {tabValue === 1 && (
        <Box>
          <Typography variant="h5" gutterBottom sx={{ color: '#000000', fontWeight: 'bold', borderBottom: '2px solid #FFE302', pb: 1, fontFamily: 'Arial Black, Arial, sans-serif', letterSpacing: '0.5px' }}>
            Monthly Operating Costs
          </Typography>
          <Typography variant="body1" paragraph sx={{ fontFamily: 'Arial, sans-serif', fontSize: '16px', mb: 3 }}>
            The following table outlines the recurring monthly expenses required to maintain and grow the Warrili platform.
          </Typography>
          
          <TableContainer component={Paper} sx={{ mb: 4, borderRadius: 0, boxShadow: 'none', border: '1px solid #000000' }}>
            <Table>
              <TableHead sx={{ backgroundColor: '#000000' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', color: '#FFE302', borderBottom: 'none', fontFamily: 'Arial Black, Arial, sans-serif', fontSize: '16px' }}>Category</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#FFE302', borderBottom: 'none', fontFamily: 'Arial Black, Arial, sans-serif', fontSize: '16px' }}>Monthly Cost (DZD)</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#FFE302', borderBottom: 'none', fontFamily: 'Arial Black, Arial, sans-serif', fontSize: '16px' }}>Description</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {monthlyOperatingData.map((row, index) => (
                  <TableRow key={index} sx={{ 
                    '&:nth-of-type(odd)': { backgroundColor: '#f5f5f5' },
                    '&:hover': { backgroundColor: 'rgba(255, 227, 2, 0.05)' }
                  }}>
                    <TableCell sx={{ borderColor: '#e0e0e0', fontFamily: 'Arial, sans-serif', fontWeight: 600, fontSize: '15px' }}>{row.category}</TableCell>
                    <TableCell sx={{ borderColor: '#e0e0e0', fontFamily: 'Arial, sans-serif', fontSize: '15px' }}>{formatCurrency(row.cost)}</TableCell>
                    <TableCell sx={{ borderColor: '#e0e0e0', fontFamily: 'Arial, sans-serif', fontSize: '15px' }}>{row.description}</TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ backgroundColor: 'rgba(255, 227, 2, 0.15)' }}>
                  <TableCell sx={{ fontWeight: 'bold', borderColor: '#e0e0e0', fontFamily: 'Arial Black, Arial, sans-serif', fontSize: '16px' }}>Total Monthly Operating Costs</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', borderColor: '#e0e0e0', fontFamily: 'Arial Black, Arial, sans-serif', fontSize: '16px' }}>
                    {formatCurrency(monthlyOperatingData.reduce((sum, item) => sum + item.cost, 0))}
                  </TableCell>
                  <TableCell sx={{ borderColor: '#e0e0e0' }}></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%', borderRadius: 0, boxShadow: 'none', border: '1px solid #000000' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#000000', borderBottom: '2px solid #FFE302', pb: 1, fontFamily: 'Arial Black, Arial, sans-serif', fontSize: '18px' }}>
                    Operating Costs Breakdown
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart
                      data={monthlyOperatingData}
                      margin={{ top: 20, right: 30, left: 20, bottom: 70 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="category" 
                        angle={-45} 
                        textAnchor="end" 
                        height={80} 
                        interval={0}
                        tick={{ fontSize: 10 }}
                      />
                      <YAxis tickFormatter={(value) => value.toLocaleString()} />
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Bar dataKey="cost" fill="#000000" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%', borderRadius: 0, boxShadow: 'none', border: '1px solid #000000' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#000000', borderBottom: '2px solid #FFE302', pb: 1, fontFamily: 'Arial Black, Arial, sans-serif', fontSize: '18px' }}>
                    Operating Cost Analysis
                  </Typography>
                  <Typography variant="body2" paragraph sx={{ fontFamily: 'Arial, sans-serif', fontSize: '15px', fontWeight: 500 }}>
                    • Personnel costs represent approximately 50% of monthly operating expenses, reflecting the importance of skilled staff.
                  </Typography>
                  <Typography variant="body2" paragraph sx={{ fontFamily: 'Arial, sans-serif', fontSize: '15px', fontWeight: 500 }}>
                    • API usage fees are significant due to our reliance on Clarifai, OpenAI, and DeepAI for hardware analysis features.
                  </Typography>
                  <Typography variant="body2" paragraph sx={{ fontFamily: 'Arial, sans-serif', fontSize: '15px', fontWeight: 500 }}>
                    • Ongoing marketing is essential to maintain user growth and market presence.
                  </Typography>
                  <Typography variant="body2" paragraph sx={{ fontFamily: 'Arial, sans-serif', fontSize: '15px', fontWeight: 500 }}>
                    • Annual operating costs are estimated at {formatCurrency(monthlyOperatingData.reduce((sum, item) => sum + item.cost, 0) * 12)}.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Revenue Projections Tab */}
      {tabValue === 2 && (
        <Box>
          <Typography variant="h5" gutterBottom sx={{ color: '#000000', fontWeight: 'bold', borderBottom: '2px solid #FFE302', pb: 1, fontFamily: 'Arial Black, Arial, sans-serif', letterSpacing: '0.5px' }}>
            Revenue Projections (5-Year Plan)
          </Typography>
          <Typography variant="body1" paragraph sx={{ fontFamily: 'Arial, sans-serif', fontSize: '16px', mb: 3 }}>
            The following table outlines projected revenue streams for the Warrili platform over a five-year period.
          </Typography>
          
          <TableContainer component={Paper} sx={{ mb: 4, borderRadius: 0, boxShadow: 'none', border: '1px solid #000000' }}>
            <Table>
              <TableHead sx={{ backgroundColor: '#000000' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', color: '#FFE302', borderBottom: 'none', fontFamily: 'Arial Black, Arial, sans-serif', fontSize: '16px' }}>Period</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#FFE302', borderBottom: 'none', fontFamily: 'Arial Black, Arial, sans-serif', fontSize: '16px' }}>Subscriptions (DZD)</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#FFE302', borderBottom: 'none', fontFamily: 'Arial Black, Arial, sans-serif', fontSize: '16px' }}>Video Consultations (DZD)</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#FFE302', borderBottom: 'none', fontFamily: 'Arial Black, Arial, sans-serif', fontSize: '16px' }}>Parts Marketplace (DZD)</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#FFE302', borderBottom: 'none', fontFamily: 'Arial Black, Arial, sans-serif', fontSize: '16px' }}>Premium Services (DZD)</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#FFE302', borderBottom: 'none', fontFamily: 'Arial Black, Arial, sans-serif', fontSize: '16px' }}>Total Revenue (DZD)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {revenueProjectionData.map((row, index) => {
                  const totalRevenue = row.subscription + row.consultation + row.marketplace + row.premium;
                  return (
                    <TableRow key={index} sx={{ 
                      '&:nth-of-type(odd)': { backgroundColor: '#f5f5f5' },
                      '&:hover': { backgroundColor: 'rgba(255, 227, 2, 0.05)' }
                    }}>
                      <TableCell sx={{ borderColor: '#e0e0e0', fontFamily: 'Arial, sans-serif', fontWeight: 600, fontSize: '15px' }}>{row.year}</TableCell>
                      <TableCell sx={{ borderColor: '#e0e0e0', fontFamily: 'Arial, sans-serif', fontSize: '15px' }}>{formatCurrency(row.subscription)}</TableCell>
                      <TableCell sx={{ borderColor: '#e0e0e0', fontFamily: 'Arial, sans-serif', fontSize: '15px' }}>{formatCurrency(row.consultation)}</TableCell>
                      <TableCell sx={{ borderColor: '#e0e0e0', fontFamily: 'Arial, sans-serif', fontSize: '15px' }}>{formatCurrency(row.marketplace)}</TableCell>
                      <TableCell sx={{ borderColor: '#e0e0e0', fontFamily: 'Arial, sans-serif', fontSize: '15px' }}>{formatCurrency(row.premium)}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', borderColor: '#e0e0e0', fontFamily: 'Arial, sans-serif', fontSize: '15px' }}>{formatCurrency(totalRevenue)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%', borderRadius: 0, boxShadow: 'none', border: '1px solid #000000' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#000000', borderBottom: '2px solid #FFE302', pb: 1, fontFamily: 'Arial Black, Arial, sans-serif', fontSize: '18px' }}>
                    Revenue Growth Projection
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart
                      data={revenueProjectionData}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="year" />
                      <YAxis tickFormatter={(value) => (value / 1000000).toFixed(0) + 'M'} />
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Legend />
                      <Bar dataKey="subscription" stackId="a" fill="#000000" name="Subscriptions" />
                      <Bar dataKey="consultation" stackId="a" fill="#333333" name="Video Consultations" />
                      <Bar dataKey="marketplace" stackId="a" fill="#FFE302" name="Parts Marketplace" />
                      <Bar dataKey="premium" stackId="a" fill="#FFC107" name="Premium Services" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%', borderRadius: 0, boxShadow: 'none', border: '1px solid #000000' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#000000', borderBottom: '2px solid #FFE302', pb: 1, fontFamily: 'Arial Black, Arial, sans-serif', fontSize: '18px' }}>
                    Revenue Stream Distribution
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={revenueStreamsData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                        nameKey="name"
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      >
                        {revenueStreamsData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Pricing Model Tab */}
      {tabValue === 3 && (
        <Box>
          <Typography variant="h5" gutterBottom sx={{ color: '#000000', fontWeight: 'bold', borderBottom: '2px solid #FFE302', pb: 1, fontFamily: 'Arial Black, Arial, sans-serif', letterSpacing: '0.5px' }}>
            Pricing Model
          </Typography>
          <Typography variant="body1" paragraph sx={{ fontFamily: 'Arial, sans-serif', fontSize: '16px', mb: 3 }}>
            The following table outlines the pricing structure for different subscription tiers of the Warrili platform.
          </Typography>
          
          <TableContainer component={Paper} sx={{ mb: 4, borderRadius: 0, boxShadow: 'none', border: '1px solid #000000' }}>
            <Table>
              <TableHead sx={{ backgroundColor: '#000000' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', color: '#FFE302', borderBottom: 'none', fontFamily: 'Arial Black, Arial, sans-serif', fontSize: '16px' }}>Plan</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#FFE302', borderBottom: 'none', fontFamily: 'Arial Black, Arial, sans-serif', fontSize: '16px' }}>Price</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#FFE302', borderBottom: 'none', fontFamily: 'Arial Black, Arial, sans-serif', fontSize: '16px' }}>Features</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pricingModelData.map((row, index) => (
                  <TableRow key={index} sx={{ 
                    '&:nth-of-type(odd)': { backgroundColor: '#f5f5f5' },
                    '&:hover': { backgroundColor: 'rgba(255, 227, 2, 0.05)' }
                  }}>
                    <TableCell sx={{ fontWeight: 'bold', borderColor: '#e0e0e0', fontFamily: 'Arial, sans-serif', fontSize: '15px' }}>{row.plan}</TableCell>
                    <TableCell sx={{ borderColor: '#e0e0e0', fontFamily: 'Arial, sans-serif', fontSize: '15px' }}>{row.price}</TableCell>
                    <TableCell sx={{ borderColor: '#e0e0e0', fontFamily: 'Arial, sans-serif', fontSize: '15px' }}>{row.features}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card sx={{ borderRadius: 0, boxShadow: 'none', border: '1px solid #000000' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#000000', borderBottom: '2px solid #FFE302', pb: 1, fontFamily: 'Arial Black, Arial, sans-serif', fontSize: '18px' }}>
                    Additional Revenue Streams
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, fontFamily: 'Arial Black, Arial, sans-serif', fontSize: '16px' }}>
                        Video Consultations
                      </Typography>
                      <Typography variant="body2" paragraph sx={{ fontFamily: 'Arial, sans-serif', fontSize: '15px', fontWeight: 500 }}>
                        • Basic: 1,000 DZD per 30-minute session
                      </Typography>
                      <Typography variant="body2" paragraph sx={{ fontFamily: 'Arial, sans-serif', fontSize: '15px', fontWeight: 500 }}>
                        • Advanced: 2,500 DZD per 60-minute session
                      </Typography>
                      <Typography variant="body2" paragraph sx={{ fontFamily: 'Arial, sans-serif', fontSize: '15px', fontWeight: 500 }}>
                        • Emergency: 3,500 DZD for priority same-day consultation
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, fontFamily: 'Arial Black, Arial, sans-serif', fontSize: '16px' }}>
                        Parts Marketplace
                      </Typography>
                      <Typography variant="body2" paragraph sx={{ fontFamily: 'Arial, sans-serif', fontSize: '15px', fontWeight: 500 }}>
                        • Commission: 10% on all transactions
                      </Typography>
                      <Typography variant="body2" paragraph sx={{ fontFamily: 'Arial, sans-serif', fontSize: '15px', fontWeight: 500 }}>
                        • Featured listings: 500 DZD per week
                      </Typography>
                      <Typography variant="body2" paragraph sx={{ fontFamily: 'Arial, sans-serif', fontSize: '15px', fontWeight: 500 }}>
                        • Verified seller badge: 2,000 DZD annually
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Break-Even Analysis Tab */}
      {tabValue === 4 && (
        <Box>
          <Typography variant="h5" gutterBottom sx={{ color: '#000000', fontWeight: 'bold', borderBottom: '2px solid #FFE302', pb: 1, fontFamily: 'Arial Black, Arial, sans-serif', letterSpacing: '0.5px' }}>
            Break-Even Analysis
          </Typography>
          <Typography variant="body1" paragraph sx={{ fontFamily: 'Arial, sans-serif', fontSize: '16px', mb: 3 }}>
            The following chart and table illustrate the projected break-even point for the Warrili platform.
          </Typography>
          
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12}>
              <Card sx={{ borderRadius: 0, boxShadow: 'none', border: '1px solid #000000' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#000000', borderBottom: '2px solid #FFE302', pb: 1, fontFamily: 'Arial Black, Arial, sans-serif', fontSize: '18px' }}>
                    Break-Even Projection
                  </Typography>
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart
                      data={breakEvenData}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis tickFormatter={(value) => (value / 1000000).toFixed(1) + 'M'} />
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Legend />
                      <Bar dataKey="revenue" fill="#000000" name="Revenue" />
                      <Bar dataKey="costs" fill="#FFE302" name="Costs" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <TableContainer component={Paper} sx={{ mb: 4, borderRadius: 0, boxShadow: 'none', border: '1px solid #000000' }}>
            <Table>
              <TableHead sx={{ backgroundColor: '#000000' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', color: '#FFE302', borderBottom: 'none', fontFamily: 'Arial Black, Arial, sans-serif', fontSize: '16px' }}>Period</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#FFE302', borderBottom: 'none', fontFamily: 'Arial Black, Arial, sans-serif', fontSize: '16px' }}>Monthly Revenue (DZD)</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#FFE302', borderBottom: 'none', fontFamily: 'Arial Black, Arial, sans-serif', fontSize: '16px' }}>Monthly Costs (DZD)</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#FFE302', borderBottom: 'none', fontFamily: 'Arial Black, Arial, sans-serif', fontSize: '16px' }}>Monthly Profit/Loss (DZD)</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#FFE302', borderBottom: 'none', fontFamily: 'Arial Black, Arial, sans-serif', fontSize: '16px' }}>Cumulative Profit/Loss (DZD)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {breakEvenData.map((row, index) => {
                  const monthlyProfitLoss = row.revenue - row.costs;
                  
                  // Calculate cumulative profit/loss
                  let cumulativeProfitLoss = 0;
                  for (let i = 0; i <= index; i++) {
                    cumulativeProfitLoss += breakEvenData[i].revenue - breakEvenData[i].costs;
                  }
                  
                  // Add initial investment to first month's cumulative loss
                  if (index === 0) {
                    cumulativeProfitLoss -= initialInvestmentData.reduce((sum, item) => sum + item.cost, 0);
                  }
                  
                  return (
                    <TableRow key={index} sx={{ 
                      '&:nth-of-type(odd)': { backgroundColor: '#f5f5f5' },
                      '&:hover': { backgroundColor: 'rgba(255, 227, 2, 0.05)' },
                      backgroundColor: cumulativeProfitLoss >= 0 && index > 0 ? 'rgba(255, 227, 2, 0.15)' : 'inherit'
                    }}>
                      <TableCell sx={{ borderColor: '#e0e0e0', fontFamily: 'Arial, sans-serif', fontWeight: 600, fontSize: '15px' }}>{row.month}</TableCell>
                      <TableCell sx={{ borderColor: '#e0e0e0', fontFamily: 'Arial, sans-serif', fontSize: '15px' }}>{formatCurrency(row.revenue)}</TableCell>
                      <TableCell sx={{ borderColor: '#e0e0e0', fontFamily: 'Arial, sans-serif', fontSize: '15px' }}>{formatCurrency(row.costs)}</TableCell>
                      <TableCell sx={{ 
                        color: monthlyProfitLoss >= 0 ? '#000000' : '#FF0000',
                        fontWeight: 'bold',
                        borderColor: '#e0e0e0', 
                        fontFamily: 'Arial, sans-serif', 
                        fontSize: '15px'
                      }}>
                        {monthlyProfitLoss >= 0 ? '+' : ''}{formatCurrency(monthlyProfitLoss)}
                      </TableCell>
                      <TableCell sx={{ 
                        color: cumulativeProfitLoss >= 0 ? '#000000' : '#FF0000',
                        fontWeight: 'bold',
                        borderColor: '#e0e0e0', 
                        fontFamily: 'Arial, sans-serif', 
                        fontSize: '15px'
                      }}>
                        {cumulativeProfitLoss >= 0 ? '+' : ''}{formatCurrency(cumulativeProfitLoss)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Card sx={{ borderRadius: 0, boxShadow: 'none', border: '1px solid #000000', mb: 4 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#000000', borderBottom: '2px solid #FFE302', pb: 1, fontFamily: 'Arial Black, Arial, sans-serif', fontSize: '18px' }}>
                Break-Even Analysis Summary
              </Typography>
              <Typography variant="body2" paragraph sx={{ fontFamily: 'Arial, sans-serif', fontSize: '15px', fontWeight: 500 }}>
                • Initial investment: {formatCurrency(initialInvestmentData.reduce((sum, item) => sum + item.cost, 0))}
              </Typography>
              <Typography variant="body2" paragraph sx={{ fontFamily: 'Arial, sans-serif', fontSize: '15px', fontWeight: 500 }}>
                • Monthly operating costs: {formatCurrency(monthlyOperatingData.reduce((sum, item) => sum + item.cost, 0))}
              </Typography>
              <Typography variant="body2" paragraph sx={{ fontFamily: 'Arial, sans-serif', fontSize: '15px', fontWeight: 500 }}>
                • Projected break-even point: Month 24 (2 years)
              </Typography>
              <Typography variant="body2" paragraph sx={{ fontFamily: 'Arial, sans-serif', fontSize: '15px', fontWeight: 500 }}>
                • After reaching break-even, the platform is projected to generate increasing profits, with an estimated annual profit of {formatCurrency(12 * (breakEvenData[breakEvenData.length - 1].revenue - breakEvenData[breakEvenData.length - 1].costs))} by Year 3.
              </Typography>
            </CardContent>
          </Card>
        </Box>
      )}

      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <Button 
          variant="contained" 
          sx={{ 
            bgcolor: '#000000', 
            color: '#FFE302',
            '&:hover': { bgcolor: '#333333' },
            px: 4,
            py: 1.5,
            borderRadius: 0,
            fontFamily: 'Arial Black, Arial, sans-serif',
            fontSize: '16px',
            fontWeight: 'bold'
          }}
        >
          Download Full Financial Plan
        </Button>
      </Box>
    </Container>
  );
};

export default Costs;